import { orderByProductOrder } from '@provision/membership-plans-ui';
import { sortBy } from 'remeda';
import type { BundledActivationFlowConfig } from 'src/graphql';
import { formatAmountCents, isValidFiniteNumber } from 'src/utils/number';

export const FIRST_COL_FIELD_NAME = 'firstCol';

export type PricingExportColumn = {
  field: string;
  headerName: string;
  align?: 'left' | 'center' | 'right';
};

export type PricingExportRow = Record<string, string | undefined>;

const buildProductFieldName = (productId: string) => `product_${productId}`;

type BuildPriceRowParams = {
  label: string;
  priceKey: 'monthly' | 'annual';
  products: BundledActivationFlowConfig['products'];
  productFieldMap: Record<string, string>;
};

const buildPriceRow = ({
  label,
  priceKey,
  products,
  productFieldMap,
}: BuildPriceRowParams) => {
  const row: PricingExportRow = {
    [FIRST_COL_FIELD_NAME]: label,
  };

  products.forEach((product) => {
    const fieldName = productFieldMap[product.id];
    if (!fieldName) {
      return;
    }

    if (product.callForPricing) {
      row[fieldName] = 'Call for Pricing';
      return;
    }

    const price = product.prices[priceKey];
    const amountCents = price?.amountCents;
    const normalizedAmount = isValidFiniteNumber(amountCents) ? amountCents : 0;
    row[fieldName] = formatAmountCents(normalizedAmount, '');
  });

  return row;
};

export const buildPricingExportGrid = (config: BundledActivationFlowConfig) => {
  const products = Array.isArray(config.products) ? config.products : [];
  const features = Array.isArray(config.features) ? config.features : [];
  const options = config.options as
    | (BundledActivationFlowConfig['options'] & { showMonthlyPrices?: boolean })
    | undefined;

  const sortedProducts = orderByProductOrder(products, options?.productOrder, (product) => ({
    id: product.id,
    name: product.name,
  }));

  const productFieldMap = sortedProducts.reduce<Record<string, string>>((acc, product) => {
    acc[product.id] = buildProductFieldName(product.id);
    return acc;
  }, {});

  const columns: PricingExportColumn[] = [
    {
      field: FIRST_COL_FIELD_NAME,
      headerName: '',
    },
    ...sortedProducts.map((product) => ({
      field: productFieldMap[product.id],
      headerName: product.name,
    })),
  ];

  const rows: PricingExportRow[] = [];
  const showMonthlyPrices = options?.showMonthlyPrices ?? true;
  const showAnnualPrices = options?.showAnnualPrices ?? true;

  if (showMonthlyPrices) {
    rows.push(
      buildPriceRow({
        label: 'Monthly',
        priceKey: 'monthly',
        products: sortedProducts,
        productFieldMap,
      }),
    );
  }

  if (showAnnualPrices) {
    rows.push(
      buildPriceRow({
        label: 'Annual',
        priceKey: 'annual',
        products: sortedProducts,
        productFieldMap,
      }),
    );
  }

  const sortedFeatures = sortBy(features, [(feature) => feature.sortOrder ?? 0, 'asc']);

  sortedFeatures.forEach((feature) => {
    const row: PricingExportRow = {
      [FIRST_COL_FIELD_NAME]: '',
    };
    let hasIncludedPlan = false;

    sortedProducts.forEach((product) => {
      const fieldName = productFieldMap[product.id];
      const activeFeature = product.activeFeatures.find(
        (entry) => entry.membershipFeatureId === feature.membershipFeatureId,
      );

      if (!activeFeature || !fieldName) {
        return;
      }

      hasIncludedPlan = true;
      const overrideContent = activeFeature.overrideContent?.trim();
      row[fieldName] = overrideContent ?? feature.defaultContent ?? '';
    });

    if (!hasIncludedPlan) {
      return;
    }

    rows.push(row);
  });

  return {
    columns,
    rows,
  };
};
